import type { MotivoCancelacion } from '../../features/visitas/MotivoCancelacion';
import { baseApi } from './baseApi';
import type { PaginationMeta } from '../../types';

import type { CurrencyCode } from '../../utils/moneda';
// Resumen liviano que devuelve GET /customers/:id/card (network-wide, campos seguros)
export type ContactType = 'direct' | 'interested' | 'colleague' | 'owner';

export interface CustomerCardSummary {
  _id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: 'active' | 'closed' | 'discarded';
  source: string | null;
  tags: string[];
  isInmobiliaria?: boolean;
  /** Qué es el contacto: cliente directo, interesado por una propiedad, colega o dueño. */
  contactType?: ContactType;
  agent: string | null;
}

// Solicitud de cambio sobre un cliente (gate de aprobación del dueño). (Nahomis)
export interface CustomerFieldChange {
  field: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
}
export interface CustomerChangeRequest {
  _id: string;
  customerId: string;
  customerName: string;
  requestedBy: string;
  requestedByName: string;
  ownerId: string;
  changes: CustomerFieldChange[];
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}
// El update de un cliente puede devolver el cliente actualizado (dueño/admin) o, si lo
// edita un compañero, una solicitud pendiente de aprobación.
export interface ChangeRequestPending {
  pending: true;
  applied: false;
  requestId?: string;
  message: string;
  changes?: CustomerFieldChange[];
}

export interface ImportantDate {
  label: string;              // "Cumpleaños", "Aniversario", etc.
  date: string;               // ISO
  recurring: boolean;         // se repite cada año
  note?: string;
  remindDaysBefore?: number;  // avisar N días antes (0 = el mismo día)
}

export interface CustomerInterest {
  operation: 'sale' | 'rent';
  type: string;
  minPrice?: number;
  maxPrice?: number;
  /** Moneda de min/maxPrice. Sin ella es un pedido viejo, cargado en U$S. */
  currency?: 'USD' | 'UYU';
  minArea?: number;
  maxArea?: number;
  /** @deprecated mínimo — lo reemplaza bedroomOptions. Queda para pedidos viejos. */
  bedrooms?: number;
  /** Cantidades que le sirven al cliente: 'mono' | '1' | '2' | '3' | '4plus'. */
  bedroomOptions?: string[];
  /** Texto legible de los dormitorios ("1 o 2 dormitorios"). Lo calcula el backend
   *  —única fuente— para que la ficha y el aviso de coincidencia no diverjan. */
  bedroomsText?: string;
  // "Más filtros": ninguno obligatorio. Una propiedad sin el dato cargado no queda
  // excluida del match (ver passesExtraFilters en el backend).
  /** Baños que sirven: '1' | '2' | '3' | '4' | '5plus'. */
  bathroomOptions?: string[];
  /** Cocheras como MÍNIMO. */
  garagesMin?: number;
  minCoveredArea?: number;
  maxCoveredArea?: number;
  /** Antigüedad máxima: 'new' | '5' | '10' | '20'. */
  maxAge?: string;
  furnished?: boolean;
  /** Tiene mascota: solo propiedades que las admitan. */
  petsAllowed?: boolean;
  /** ALQUILER: garantías que el cliente puede presentar. */
  guarantees?: string[];
  /** VENTA: el cliente necesita financiación bancaria. */
  needsBankFinancing?: boolean;
  zones: string[];
  notes?: string;
  /** Punto marcado en el mapa al cargar el pedido (para la vista mapa del match). */
  lat?: number;
  lng?: number;
}

export interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  secondaryPhone?: string;
  address?: string;
  city?: string;
  country?: string;
  status: 'active' | 'closed' | 'discarded';
  /**
   * Si la ficha se unificó con otra, el id de la que quedó viva. La perdedora queda sin
   * teléfono (el índice único no admite dos), así que quien necesite el número tiene que
   * seguir esta flecha. (ver clienteVigente en el backend)
   */
  mergedInto?: string;
  source: string;
  tags: string[];
  isInmobiliaria?: boolean;
  contactType?: ContactType;
  /** Última vez que se registró contacto (semáforo de "Sin contactar"). */
  lastContactedAt?: string;
  privateDescription?: string;
  assignedTo: string;
  sharedWith?: string[];
  office: string;
  assignedToSnapshot: { name: string; lastName: string; avatar?: string };
  /** Estado EN VIVO del agente dueño (solo en el detalle): sirve para saber, al agregar una
   *  oportunidad sobre un cliente de otro agente, si ese colega sigue activo o ya no está. */
  assignedToActive?: boolean;
  /** Teléfono EN VIVO del agente dueño (solo en el detalle), para escribirle sin buscarlo. */
  assignedToPhone?: string;
  interests: CustomerInterest[];
  importantDates?: ImportantDate[];
  propertiesOfInterest?: string[];
  /** Por dónde entró el contacto: cargado a mano, por Gustavo o por el Agente Virtual. */
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  createdAt: string;
  updatedAt: string;
}

/** Un vínculo ya resuelto: el otro cliente y qué es de este. */
export interface CustomerRelation {
  _id: string;
  kind: 'conyuge' | 'ex_pareja' | 'hermano' | 'padre' | 'hijo' | 'socio' | 'amigo' | 'otro';
  note: string | null;
  createdAt: string;
  customer: { _id: string; firstName: string; lastName: string; phone: string | null; agente: string | null };
}

/** Un aviso del antivuelco: alguien de la familia ya está trabajado por otro agente. */
export interface AvisoAntivuelco {
  motivo: 'vinculo' | 'mismo_telefono';
  vinculoLabel?: string;
  otroCliente: { _id: string; nombre: string };
  agente: { _id: string; nombre: string };
  donde: 'captacion' | 'propiedad';
  referencia: { _id: string; titulo: string };
}

export interface CustomerNote {
  _id: string;
  customer: string;
  property?: string;
  content: string;
  contentPlain: string;
  isPinned: boolean;
  visibleToOwner?: boolean;
  createdBySnapshot: { name: string; lastName: string; avatar?: string };
  createdAt: string;
  updatedAt: string;
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  aiConversationId?: string;
}

export interface CustomerEvent {
  _id: string;
  customer: string;
  type: 'visit' | 'call' | 'meeting' | 'showing' | 'offer' | 'inquiry';
  title: string;
  description?: string;
  date: string;
  duration?: number;
  location?: string;
  outcome?: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  createdAt: string;
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  /** ObjectId de quien lo generó. */
  createdBy?: string;
  /**
   * Nombre de quien lo generó, ya compuesto por el backend. Puede faltar si el usuario
   * fue borrado; entonces la fila no dice autor, en vez de mostrar un id.
   */
  createdByName?: string;
  createdByAvatar?: string;
  aiConversationId?: string;
  property?: string;       // ObjectId de la propiedad asociada (si aplica)
  propertyCode?: string;   // código humano (ej. IMP-2026-2048) para mostrar/enlazar
  /** Título de la propiedad: el del evento suele ser genérico. Ver utils/eventTitle. */
  propertyTitle?: string;
  /** Operación de la propiedad: define con qué palabras se cierra la visita. */
  propertyOperation?: string;
  /** Por qué se cayó, al cancelar (ver features/visitas/MotivoCancelacion). */
  cancellationCategory?: MotivoCancelacion;
  cancellationReason?: string;
}

export interface CustomerOpportunity {
  _id: string;
  customer: string;
  title: string;
  stage: 'initial_contact' | 'interested' | 'negotiating' | 'offer_sent' | 'closed_won' | 'closed_lost';
  value?: number;
  currency: CurrencyCode;
  probability?: number;
  expectedCloseDate?: string;
  notes?: string;
  lostReason?: string;
  createdAt: string;
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  aiConversationId?: string;
  createdBy?: string;
  /** Nombre del agente dueño (createdBy resuelto) — para ver de qué colega es. */
  ownerName?: string | null;
}

export interface CustomerReminder {
  _id: string;
  customer: string;
  title: string;
  description?: string;
  dueDate: string;
  isCompleted: boolean;
  completedAt?: string;
  notifyVia: string[];
  assignedTo: string;
  createdAt: string;
  createdVia?: 'gustavo' | 'ui' | 'phylax';
  aiConversationId?: string;
}

export interface GetCustomersParams {
  page?: number;
  limit?: number;
  status?: string;
  source?: string;
  tags?: string;
  assignedTo?: string;
  office?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  globalSearch?: boolean;
}

/** Los recordatorios personales no cuelgan de ninguna ficha: solo refrescan la agenda. */
const reminderTags = (customerId?: string) =>
  (customerId
    ? [{ type: 'Customer', id: `${customerId}-reminders` }, 'TodayAgenda']
    : ['TodayAgenda']) as never[];

export const customersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Customers
    getCustomers: build.query<{ data: Customer[]; pagination: PaginationMeta }, GetCustomersParams>({
      query: (params) => ({ url: '/customers', params }),
      transformResponse: (res: { data: Customer[]; pagination: PaginationMeta }) => res,
      providesTags: (result) =>
        result
          ? [...result.data.map(({ _id }) => ({ type: 'Customer' as const, id: _id })), { type: 'Customer', id: 'LIST' }]
          : [{ type: 'Customer', id: 'LIST' }],
    }),

    getCustomerById: build.query<Customer, string>({
      query: (id) => `/customers/${id}`,
      transformResponse: (res: { data: Customer }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id }],
    }),

    // Resumen liviano para la card inline de Gustavo: lectura network-wide
    // (no scopeada por oficina), solo campos seguros para la tarjeta.
    getCustomerCard: build.query<CustomerCardSummary, string>({
      query: (id) => `/customers/${id}/card`,
      transformResponse: (res: { data: CustomerCardSummary }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `card-${id}` }],
    }),

    createCustomer: build.mutation<Customer, Partial<Customer> & { firstName: string }>({
      query: (body) => ({ url: '/customers', method: 'POST', body }),
      transformResponse: (res: { data: Customer }) => res.data,
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    // Vincular un cliente existente a la cartera del agente (flujo de contacto duplicado)
    linkCustomer: build.mutation<Customer, string>({
      query: (id) => ({ url: `/customers/${id}/link`, method: 'POST' }),
      transformResponse: (res: { data: Customer }) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    updateCustomer: build.mutation<Customer | ChangeRequestPending, { id: string; body: Partial<Customer> }>({
      query: ({ id, body }) => ({ url: `/customers/${id}`, method: 'PUT', body }),
      transformResponse: (res: { data: Customer | ChangeRequestPending }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    // ─── Solicitudes de cambio (gate de aprobación del dueño) ──────────────
    getPendingChangeRequests: build.query<CustomerChangeRequest[], void>({
      query: () => ({ url: '/customers/change-requests/pending' }),
      transformResponse: (res: { data: CustomerChangeRequest[] }) => res.data,
      providesTags: [{ type: 'Customer', id: 'CHANGE_REQUESTS' }],
    }),
    resolveChangeRequest: build.mutation<{ id: string; status: string }, { reqId: string; approve: boolean }>({
      query: ({ reqId, approve }) => ({ url: `/customers/change-requests/${reqId}/resolve`, method: 'PUT', body: { approve } }),
      transformResponse: (res: { data: { id: string; status: string } }) => res.data,
      invalidatesTags: [{ type: 'Customer', id: 'CHANGE_REQUESTS' }, { type: 'Customer', id: 'LIST' }],
    }),

    // Pedidos (interests): el endpoint reemplaza todo el array; para agregar uno se
    // manda existentes + el nuevo. (carga manual de pedidos)
    upsertInterests: build.mutation<Customer, { id: string; interests: CustomerInterest[] }>({
      query: ({ id, interests }) => ({ url: `/customers/${id}/interests`, method: 'PUT', body: { interests } }),
      transformResponse: (res: { data: Customer }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    upsertImportantDates: build.mutation<Customer, { id: string; importantDates: ImportantDate[] }>({
      query: ({ id, importantDates }) => ({ url: `/customers/${id}/important-dates`, method: 'PUT', body: { importantDates } }),
      transformResponse: (res: { data: Customer }) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    updateCustomerStatus: build.mutation<Customer, { id: string; status: string }>({
      query: ({ id, status }) => ({ url: `/customers/${id}/status`, method: 'PUT', body: { status } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    reassignCustomer: build.mutation<void, { id: string; assignedTo: string }>({
      query: ({ id, assignedTo }) => ({ url: `/customers/${id}/assign`, method: 'PUT', body: { assignedTo } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    deleteCustomer: build.mutation<void, string>({
      query: (id) => ({ url: `/customers/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    getCustomerDependencies: build.query<{
      total: number;
      breakdown: {
        notes: number; events: number; opportunities: number; reminders: number;
        calendarEvents: number; properties: number; tasaciones: number; captaciones: number;
      };
    }, string>({
      query: (id) => `/customers/${id}/dependencies`,
      transformResponse: (res: { data: { total: number; breakdown: Record<string, number> } }) =>
        res.data as { total: number; breakdown: { notes: number; events: number; opportunities: number; reminders: number; calendarEvents: number; properties: number; tasaciones: number; captaciones: number } },
    }),

    hardDeleteCustomer: build.mutation<{ deleted: true }, string>({
      query: (id) => ({ url: `/customers/${id}/hard`, method: 'DELETE' }),
      transformResponse: (res: { data: { deleted: true } }) => res.data,
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    // Notes
    // ─── Vínculos entre clientes (hermano, cónyuge, ex…) ──────────────────
    getCustomerRelations: build.query<CustomerRelation[], string>({
      query: (id) => `/customers/${id}/relations`,
      transformResponse: (res: { data: CustomerRelation[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `REL-${id}` }],
    }),

    createCustomerRelation: build.mutation<CustomerRelation[], { customerId: string; relatedCustomerId: string; kind: string; note?: string }>({
      query: ({ customerId, ...body }) => ({ url: `/customers/${customerId}/relations`, method: 'POST', body }),
      transformResponse: (res: { data: CustomerRelation[] }) => res.data,
      // Invalida las dos fichas: el vínculo se guarda en los dos sentidos.
      invalidatesTags: (_r, _e, a) => [
        { type: 'Customer', id: `REL-${a.customerId}` },
        { type: 'Customer', id: `REL-${a.relatedCustomerId}` },
      ],
    }),

    deleteCustomerRelation: build.mutation<CustomerRelation[], { customerId: string; relationId: string; relatedCustomerId?: string }>({
      query: ({ customerId, relationId }) => ({ url: `/customers/${customerId}/relations/${relationId}`, method: 'DELETE' }),
      transformResponse: (res: { data: CustomerRelation[] }) => res.data,
      invalidatesTags: (_r, _e, a) => [
        { type: 'Customer', id: `REL-${a.customerId}` },
        ...(a.relatedCustomerId ? [{ type: 'Customer' as const, id: `REL-${a.relatedCustomerId}` }] : []),
      ],
    }),

    /** Quién más está trabajando a este titular (o a un familiar suyo). Solo avisa. */
    getAntivuelco: build.query<AvisoAntivuelco[], string>({
      query: (id) => `/customers/${id}/antivuelco`,
      transformResponse: (res: { data: AvisoAntivuelco[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `REL-${id}` }],
    }),

    getCustomerNotes: build.query<CustomerNote[], string>({
      query: (id) => `/customers/${id}/notes`,
      transformResponse: (res: { data: CustomerNote[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `${id}-notes` }],
    }),

    createCustomerNote: build.mutation<CustomerNote, { customerId: string; content: string; contentPlain: string; isPinned?: boolean; visibleToOwner?: boolean; property?: string }>({
      query: ({ customerId, ...body }) => ({ url: `/customers/${customerId}/notes`, method: 'POST', body }),
      transformResponse: (res: { data: CustomerNote }) => res.data,
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-notes` }],
    }),

    updateCustomerNote: build.mutation<CustomerNote, { customerId: string; noteId: string; body: Partial<CustomerNote> }>({
      query: ({ customerId, noteId, body }) => ({ url: `/customers/${customerId}/notes/${noteId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-notes` }],
    }),

    deleteCustomerNote: build.mutation<void, { customerId: string; noteId: string }>({
      query: ({ customerId, noteId }) => ({ url: `/customers/${customerId}/notes/${noteId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-notes` }],
    }),

    // Events
    getCustomerEvents: build.query<CustomerEvent[], string>({
      query: (id) => `/customers/${id}/events`,
      transformResponse: (res: { data: CustomerEvent[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `${id}-events` }],
    }),

    createCustomerEvent: build.mutation<CustomerEvent, { customerId: string } & Omit<CustomerEvent, '_id' | 'customer' | 'createdAt'> & { propertyId?: string }>({
      query: ({ customerId, ...body }) => ({ url: `/customers/${customerId}/events`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { customerId, propertyId }) => {
        const tags: Array<{ type: 'Customer' | 'Property'; id: string }> = [
          { type: 'Customer', id: `${customerId}-events` },
        ];
        if (propertyId) tags.push({ type: 'Property', id: `crm-${propertyId}` });
        return tags;
      },
    }),

    updateCustomerEvent: build.mutation<CustomerEvent, { customerId: string; eventId: string; body: Partial<CustomerEvent> }>({
      query: ({ customerId, eventId, body }) => ({ url: `/customers/${customerId}/events/${eventId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-events` }],
    }),

    deleteCustomerEvent: build.mutation<void, { customerId: string; eventId: string }>({
      query: ({ customerId, eventId }) => ({ url: `/customers/${customerId}/events/${eventId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-events` }],
    }),

    // Opportunities
    getCustomerOpportunities: build.query<CustomerOpportunity[], string>({
      query: (id) => `/customers/${id}/opportunities`,
      transformResponse: (res: { data: CustomerOpportunity[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `${id}-opps` }],
    }),

    createCustomerOpportunity: build.mutation<CustomerOpportunity, { customerId: string } & Partial<CustomerOpportunity> & { propertyId?: string }>({
      query: ({ customerId, ...body }) => ({ url: `/customers/${customerId}/opportunities`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { customerId, propertyId }) => {
        const tags: Array<{ type: 'Customer' | 'Property'; id: string }> = [
          { type: 'Customer', id: `${customerId}-opps` },
        ];
        if (propertyId) tags.push({ type: 'Property', id: `crm-${propertyId}` });
        return tags;
      },
    }),

    updateCustomerOpportunity: build.mutation<CustomerOpportunity, { customerId: string; oppId: string; body: Partial<CustomerOpportunity> }>({
      query: ({ customerId, oppId, body }) => ({ url: `/customers/${customerId}/opportunities/${oppId}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-opps` }],
    }),

    deleteCustomerOpportunity: build.mutation<void, { customerId: string; oppId: string }>({
      query: ({ customerId, oppId }) => ({ url: `/customers/${customerId}/opportunities/${oppId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-opps` }],
    }),

    // Reminders
    getCustomerReminders: build.query<CustomerReminder[], string>({
      query: (id) => `/customers/${id}/reminders`,
      transformResponse: (res: { data: CustomerReminder[] }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `${id}-reminders` }],
    }),

    createCustomerReminder: build.mutation<CustomerReminder, { customerId: string } & Partial<CustomerReminder> & { dueDate: string; title: string }>({
      query: ({ customerId, ...body }) => ({ url: `/customers/${customerId}/reminders`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-reminders` }, 'TodayAgenda' as never],
    }),

    /**
     * Editar un recordatorio (título, descripción y sobre todo `dueDate` → posponer).
     * Sin `customerId` (recordatorio personal, sin ficha) va por la ruta plana:
     * antes armaba `/customers/undefined/...` y el backend devolvía 500.
     */
    updateCustomerReminder: build.mutation<CustomerReminder, { customerId?: string; reminderId: string; body: Partial<CustomerReminder> }>({
      query: ({ customerId, reminderId, body }) => ({
        url: customerId
          ? `/customers/${customerId}/reminders/${reminderId}`
          : `/customers/reminders/${reminderId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { customerId }) => reminderTags(customerId),
    }),

    completeCustomerReminder: build.mutation<CustomerReminder, { customerId?: string; reminderId: string }>({
      query: ({ customerId, reminderId }) => ({
        url: customerId
          ? `/customers/${customerId}/reminders/${reminderId}/complete`
          : `/customers/reminders/${reminderId}/complete`,
        method: 'PUT',
      }),
      invalidatesTags: (_r, _e, { customerId }) => reminderTags(customerId),
    }),

    deleteCustomerReminder: build.mutation<void, { customerId: string; reminderId: string }>({
      query: ({ customerId, reminderId }) => ({ url: `/customers/${customerId}/reminders/${reminderId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-reminders` }, 'TodayAgenda' as never],
    }),

    /* ── Portal del Propietario ─────────────────────────────────────── */
    getCustomerPortalStatus: build.query<CustomerPortalStatus, string>({
      query: (customerId) => `/customers/${customerId}/portal-access`,
      transformResponse: (res: { data: CustomerPortalStatus }) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Customer', id: `${id}-portal` }],
    }),

    grantCustomerPortalAccess: build.mutation<GrantPortalResult, { customerId: string; propertyIds: string[] }>({
      query: ({ customerId, propertyIds }) => ({
        url: `/customers/${customerId}/portal-access`,
        method: 'POST',
        body: { propertyIds },
      }),
      transformResponse: (res: { data: GrantPortalResult }) => res.data,
      invalidatesTags: (_r, _e, { customerId }) => [{ type: 'Customer', id: `${customerId}-portal` }],
    }),

    resetCustomerPortalPassword: build.mutation<{ email: string; tempPassword: string }, string>({
      query: (customerId) => ({ url: `/customers/${customerId}/portal-access/reset`, method: 'PUT' }),
      transformResponse: (res: { data: { email: string; tempPassword: string } }) => res.data,
      invalidatesTags: (_r, _e, customerId) => [{ type: 'Customer', id: `${customerId}-portal` }],
    }),

    /**
     * Link de acceso directo al portal, para mandárselo por WhatsApp. Un solo uso y 60
     * minutos; NO le cambia la contraseña al dueño, así que la suya sigue sirviendo.
     */
    customerPortalMagicLink: build.mutation<
      { url: string; ttlMinutos: number; phone: string | null; firstName: string },
      string
    >({
      query: (customerId) => ({ url: `/customers/${customerId}/portal-access/magic-link`, method: 'POST' }),
      transformResponse: (res: { data: { url: string; ttlMinutos: number; phone: string | null; firstName: string } }) => res.data,
    }),

    revokeCustomerPortalAccess: build.mutation<{ ok: boolean }, string>({
      query: (customerId) => ({ url: `/customers/${customerId}/portal-access`, method: 'DELETE' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: (_r, _e, customerId) => [{ type: 'Customer', id: `${customerId}-portal` }],
    }),
  }),
});

export interface CustomerPortalStatus {
  hasAccess: boolean;
  isActive?: boolean;
  email?: string;
  mustChangePassword?: boolean;
  linkedProperties?: string[];
}

export interface GrantPortalResult {
  userId: string;
  email: string;
  tempPassword: string;
  propertiesLinked: number;
}

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerByIdQuery,
  useGetCustomerCardQuery,
  useCreateCustomerMutation,
  useLinkCustomerMutation,
  useUpdateCustomerMutation,
  useGetPendingChangeRequestsQuery,
  useResolveChangeRequestMutation,
  useUpsertInterestsMutation,
  useUpsertImportantDatesMutation,
  useUpdateCustomerStatusMutation,
  useReassignCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomerDependenciesQuery,
  useLazyGetCustomerDependenciesQuery,
  useHardDeleteCustomerMutation,
  useGetCustomerRelationsQuery,
  useCreateCustomerRelationMutation,
  useDeleteCustomerRelationMutation,
  useGetAntivuelcoQuery,
  useGetCustomerNotesQuery,
  useCreateCustomerNoteMutation,
  useUpdateCustomerNoteMutation,
  useDeleteCustomerNoteMutation,
  useGetCustomerEventsQuery,
  useCreateCustomerEventMutation,
  useUpdateCustomerEventMutation,
  useDeleteCustomerEventMutation,
  useGetCustomerOpportunitiesQuery,
  useCreateCustomerOpportunityMutation,
  useUpdateCustomerOpportunityMutation,
  useDeleteCustomerOpportunityMutation,
  useGetCustomerRemindersQuery,
  useCreateCustomerReminderMutation,
  useUpdateCustomerReminderMutation,
  useCompleteCustomerReminderMutation,
  useDeleteCustomerReminderMutation,
  useGetCustomerPortalStatusQuery,
  useGrantCustomerPortalAccessMutation,
  useResetCustomerPortalPasswordMutation,
  useCustomerPortalMagicLinkMutation,
  useRevokeCustomerPortalAccessMutation,
} = customersApi;
